import { RefreshTokenResponse } from '../../shared/models/ad/refresh-token-response.model';
import { RefreshTokenRequest } from '../../shared/models/ad/refresh-token-request.model';
import { AdAuthService } from '../services/ad-auth.service';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration, MsalService, ProtectedResourceScopes } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser';
import { catchError, EMPTY, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { UserAuthService } from 'src/app/shared/services/user-auth.service';
import { TelemetryService } from 'src/app/shared/services/telemetry.service';
import { AuthType } from 'src/app/shared/enum/auth-type.enum';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  /** The single in-flight AD token refresh, shared by every request that 401s while it runs. */
  private adTokenRefresh$: Observable<string> | null = null;
  private isRedirecting = false;

  constructor(
    private readonly userAuthService: UserAuthService,
    private readonly adAuthService: AdAuthService,
    private readonly msalService: MsalService,
    private readonly telemetryService: TelemetryService,
    @Inject(MSAL_INTERCEPTOR_CONFIG) private readonly msalInterceptorConfig: MsalInterceptorConfiguration
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    /* Bypass the app-version request */
    if (request.url.includes('/app-version/ui')) {
      return next.handle(request);
    }

    /* Bypass AD auth endpoints themselves, so a refresh/sign-in call never gets routed
       back through the 401 handling it's part of servicing. */
    if (request.url.includes('/auth/refreshToken') || request.url.includes('/auth/signIn')) {
      return next.handle(request);
    }

    /* AD auth path */
    if (this.userAuthService.hasADCredentials()) {
      return this.handleAdAuth(request, next);
    }

    /* MSAL/Azure AD auth path */
    if (this.userAuthService.hasAzureCredentials()) {
      return this.handleMsalAuth(request, next);
    }

    return next.handle(request);
  }

  private handleAdAuth(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const accessToken = this.userAuthService.getAdToken();
    const requestPath = this.getSafeRequestPath(request.url);
    const authRequest = request.clone({
      headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${accessToken}`)
    });

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        /* On 401, attempt a token refresh and retry the original request */
        if (error.status === 401 && this.userAuthService.hasADCredentials()) {
          return this.refreshAdToken(requestPath, error.status).pipe(
            /* Only a failed *refresh* forces a logout. Placing this before switchMap keeps
               errors from the retried request itself from being treated as auth failures. */
            catchError((refreshError: HttpErrorResponse) => this.logout(refreshError, {
              source: 'http-401',
              requestPath
            })),
            switchMap((accessToken: string) => next.handle(request.clone({
              headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${accessToken}`)
            })))
          );
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes the AD access token, collapsing concurrent callers onto a single network call.
   *
   * Requests commonly fire in parallel on page load (login, features, restricted features),
   * so once the access token expires several of them 401 at once. Each one must wait for the
   * shared refresh and then retry — previously all but the first were logged out outright.
   */
  private refreshAdToken(requestPath: string, status: number): Observable<string> {
    const username = this.userAuthService.getUsername();

    if (this.adTokenRefresh$) {
      this.telemetryService.trackTokenRefreshSkipped(
        AuthType.AD,
        username,
        'joined-in-flight-refresh',
        { source: 'http-401', requestPath, status }
      );
      return this.adTokenRefresh$;
    }

    this.telemetryService.trackTokenRefreshAttempt(
      AuthType.AD,
      username,
      { source: 'http-401', requestPath, status }
    );

    const refreshRequest: RefreshTokenRequest = {
      refreshToken: this.userAuthService.getAdRefreshToken()
    };

    this.adTokenRefresh$ = this.adAuthService.refreshToken(refreshRequest).pipe(
      map((refreshResponse: RefreshTokenResponse) => {
        this.userAuthService.setAdToken(refreshResponse.accessToken);
        return refreshResponse.accessToken;
      }),
      /* Clear the slot once settled so a later expiry starts a fresh refresh. */
      finalize(() => { this.adTokenRefresh$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.adTokenRefresh$;
  }

  private handleMsalAuth(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const scopes = this.getScopesForUrl(request.url);
    const requestPath = this.getSafeRequestPath(request.url);

    /* Pass through requests not covered by the protected resource map */
    if (!scopes || scopes.length === 0) {
      return next.handle(request);
    }

    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return next.handle(request);
    }

    return this.msalService.acquireTokenSilent({ scopes, account }).pipe(
      switchMap((result: AuthenticationResult) => {
        const authRequest = request.clone({
          headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${result.accessToken}`)
        });
        return next.handle(authRequest);
      }),
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          this.telemetryService.trackTokenRefreshFailure(
            AuthType.MSAL,
            account.username,
            error.message,
            { source: 'http-request', requestPath, reason: 'interaction-required' }
          );

          /* Fall back to interactive token acquisition when silent fails */
          if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
            return this.msalService.acquireTokenPopup({ scopes, account }).pipe(
              switchMap((result: AuthenticationResult) => {
                const authRequest = request.clone({
                  headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${result.accessToken}`)
                });
                return next.handle(authRequest);
              })
            );
          } else {
            /* Guard against multiple simultaneous redirect attempts:
               only the first failed request triggers the redirect; the rest
               return EMPTY so they don't compete and cause a redirect loop. */
            if (this.isRedirecting) {
              this.telemetryService.trackTokenRefreshSkipped(
                AuthType.MSAL,
                account.username,
                'redirect-already-in-progress',
                { source: 'http-request', requestPath }
              );
              return EMPTY;
            }
            this.isRedirecting = true;
            this.telemetryService.trackTokenRefreshRedirect(
              account.username,
              { source: 'http-request', requestPath }
            );
            return this.msalService.acquireTokenRedirect({ scopes, account }).pipe(
              switchMap(() => {
                this.isRedirecting = false;
                return EMPTY;
              })
            );
          }
        }

        this.telemetryService.trackTokenRefreshFailure(
          AuthType.MSAL,
          account.username,
          this.getErrorMessage(error),
          { source: 'http-request', requestPath }
        );
        return throwError(() => error);
      })
    );
  }

  /** Finds the OAuth scopes configured for a given request URL, flattening any ProtectedResourceScopes entries. */
  private getScopesForUrl(url: string): string[] | null {
    for (const [resource, entries] of this.msalInterceptorConfig.protectedResourceMap.entries()) {
      if (url.startsWith(resource) || resource.startsWith(url)) {
        if (!entries) return null;

        const scopes = entries.flatMap((entry: string | ProtectedResourceScopes) =>
          typeof entry === 'string' ? [entry] : (entry.scopes ?? [])
        );
        return scopes.length > 0 ? scopes : null;
      }
    }
    return null;
  }

  private logout(error: HttpErrorResponse, metadata: Record<string, unknown> = {}): Observable<never> {
    this.telemetryService.trackForcedLogout(
      AuthType.AD,
      this.userAuthService.getUsername(),
      this.getHttpErrorMessage(error),
      metadata
    );
    this.userAuthService.logout();
    return throwError(() => error);
  }

  private getHttpErrorMessage(error: HttpErrorResponse): string {
    const backendMessage = error?.error?.message;
    const status = error?.status ? `HTTP ${error.status}` : 'HTTP error';
    return backendMessage ? `${status}: ${backendMessage}` : `${status}: ${error.message}`;
  }

  private getErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  private getSafeRequestPath(url: string): string {
    try {
      const parsedUrl = new URL(url, globalThis.location.origin);
      return parsedUrl.pathname;
    } catch {
      return url.split('?')[0];
    }
  }
}
